import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { LicenseSnapshot } from "@/types";

function useClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

// #254: Runtime-Aktivierung mit einem Aktivierungsschlüssel (Admin-UI).
export function useActivateLicense(): UseMutationResult<LicenseSnapshot, Error, string> {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activationKey) => client().post<LicenseSnapshot>("/license/activate", { activationKey }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("license") }),
  });
}

// #254: Sofort-Refresh gegen das Portal.
export function useRetryLicense(): UseMutationResult<LicenseSnapshot, Error, void> {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => client().post<LicenseSnapshot>("/license/retry"),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("license") }),
  });
}

// #254: Manueller Offline-Lease-Import (verifiziert + Anti-Replay serverseitig).
export function useImportLicense(): UseMutationResult<LicenseSnapshot, Error, unknown> {
  const client = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signedLease) => client().post<LicenseSnapshot>("/license/import", signedLease),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("license") }),
  });
}
